import {useCallback} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {endpoints} from '@/api/endpoints'
import {ExecutionEventType, JOB_EVENT_TYPES} from '@/types/background-jobs'
import type {ExecutionStreamEvent, JobExecutionListItem} from '@/types/background-jobs'
import {useEventSourceConnection} from './use-event-source-connection'

interface UseExecutionsListStreamOptions {
    enabled?: boolean
}

interface UseExecutionsListStreamResult {
    isConnected: boolean
    error: string | null
    disconnect: () => void
}

/**
 * Hook for subscribing to SSE events for all running executions (list view).
 * Updates React Query cache with real-time progress for running jobs.
 */
export function useExecutionsListStream({
                                            enabled = true,
                                        }: UseExecutionsListStreamOptions = {}): UseExecutionsListStreamResult {
    const queryClient = useQueryClient()

    return useEventSourceConnection({
        url: endpoints.executions.stream(),
        enabled,
        eventTypes: JOB_EVENT_TYPES,
        onEvent: useCallback((raw: unknown) => {
            const data = raw as ExecutionStreamEvent

            // Update execution in list cache. Track whether any cached list
            // actually contained the execution — if none do but some lists
            // had content, the dashboard is missing an execution that started
            // between the list fetch and the first progress event.
            let foundInAnyList = false
            let anyListHadContent = false
            queryClient.setQueriesData<{ content: JobExecutionListItem[] }>(
                {queryKey: ['job-executions']},
                (old) => {
                    if (!old?.content) return old
                    anyListHadContent = true
                    if (old.content.some((exec) => exec.id === data.executionId)) {
                        foundInAnyList = true
                    }
                    return {
                        ...old,
                        content: old.content.map((exec) =>
                            exec.id === data.executionId
                                ? {...exec, status: data.status as JobExecutionListItem['status'], progress: data.progress ?? exec.progress}
                                : exec
                        ),
                    }
                }
            )

            // Invalidate outside the updater so react-query's subsequent write
            // doesn't clear the flag (see Bug A in issue 398). Unknown-execution
            // branch mirrors useJobExecutionsStream — on progress for an
            // execution that no cached list tracks, nudge observers to refetch.
            if (anyListHadContent && !foundInAnyList) {
                queryClient.invalidateQueries({queryKey: ['job-executions']})
            }

            // Invalidate on terminal events to refresh full data
            if (data.eventType === ExecutionEventType.JOB_COMPLETED || data.eventType === ExecutionEventType.JOB_FAILED || data.eventType === ExecutionEventType.JOB_CANCELLED) {
                queryClient.invalidateQueries({queryKey: ['job-executions']})
                queryClient.invalidateQueries({queryKey: ['job-execution-stats']})
            }
        }, [queryClient]),
    })
}
